"use client";

import { useCallback, useRef } from "react";

/**
 * Hook para reproducir sonidos de notificación.
 * Usa Web Audio API como fallback si HTMLAudioElement no funciona.
 */
export function useSound() {
  const audioContextRef = useRef<AudioContext | null>(null);

  const playBeep = useCallback(() => {
    try {
      if (!audioContextRef.current) {
        const AudioContextCtor =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext })
            .webkitAudioContext;
        audioContextRef.current = new AudioContextCtor();
      }
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.frequency.setValueAtTime(800, ctx.currentTime);
      oscillator.type = "sine";
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    } catch {
      // Silent fail — sound is not critical
    }
  }, []);

  const playNotification = useCallback(() => {
    try {
      // Try HTML Audio first (best for actual sound files)
      const audio = new Audio("/sounds/notification.mp3");
      audio.volume = 0.5;
      audio.play().catch(() => {
        // Fallback: Web Audio API beep
        playBeep();
      });
    } catch {
      playBeep();
    }
  }, [playBeep]);

  return { playNotification, playBeep };
}
